<template>
  <div :class="['interval', {'with-prices': withPrices}]">
    <div class="enable-section section">
      <q-toggle
        v-model="localInterval.isEnabled"
      />
    </div>
    <div class="interval-section section">
      <div class="subtitle">
        Интервал*
      </div>
      <div class="interval-inputs">
        <q-input
          v-model="localInterval.from"
          class="input text-center small"
          square
          required
          outlined
          dense
          hide-bottom-space
          no-error-icon
          mask="time"
          :rules="[required, time]"
          :lazy-rules="lazyRules"
        />
        <q-input
          v-model="localInterval.to"
          class="input text-center small"
          square
          required
          outlined
          dense
          hide-bottom-space
          no-error-icon
          mask="time"
          :rules="[required, time]"
          :lazy-rules="lazyRules"
        />
      </div>
    </div>
    <div class="days-section section">
      <div class="subtitle">
        Дни недели и лимит заказа
      </div>
      <div class="days">
        <div
          v-for="(day, index) in localInterval.days"
          :key="index"
          class="day"
        >
          <div class="label">
            {{ day.label }}
          </div>
          <q-toggle
            v-model="day.isEnabled"
            class="radio"
            dense
          />
          <q-input
            v-model="day.limit"
            class="input text-center small"
            square
            outlined
            dense
            hide-bottom-space
            :lazy-rules="lazyRules"
            mask="###"
            no-error-icon
          />
        </div>
      </div>
    </div>
    <div class="block-section section">
      <div class="subtitle">
        Блокировка за (часов)
      </div>
      <q-input
        v-model="localInterval.blockInterval"
        class="input text-center small"
        square
        outlined
        dense
        hide-bottom-space
        mask="##"
      />
    </div>
    <div v-if="withPrices" class="section">
      <div class="subtitle">
        Сумма заказа от
      </div>
      <q-input
        v-model="localInterval.orderCostFrom"
        class="input text-center small"
        square
        outlined
        dense
        hide-bottom-space
        mask="##########"
        lazy-rules="ondemand"
      />
    </div>
    <div v-if="withPrices" class="section">
      <div class="subtitle">
        Сумма заказа до
      </div>
      <q-input
        v-model="localInterval.orderCostTo"
        class="input text-center small"
        square
        outlined
        dense
        hide-bottom-space
        mask="##########"
        lazy-rules="ondemand"
      />
    </div>
    <div v-if="withPrices" class="section">
      <div class="subtitle">
        Стоимость доставки
      </div>
      <q-input
        v-model="localInterval.deliveryCost"
        class="input text-center small"
        square
        outlined
        dense
        hide-bottom-space
        lazy-rules="ondemand"
        mask="#########"
      />
    </div>
    <div v-if="removeBtn" class="delete-section section">
      <div
        class="icon"
        @click="remove"
        v-html="DeleteIcon"
      />
    </div>
  </div>
</template>

<script>
/**
 * @module Shared/Component/Interval
 * @requires icon:DeleteIcon
 * @requires npm:lodash
 * @requires Utils/validate
 * @fires module:Shared/Component/Interval#change
 * @fires module:Shared/Component/Interval#remove
 * @vue-prop {Object} interval - Внешнее состояние интервала
 * @vue-prop {Boolean | String} lazyRules - Свойство quasar
 *   определяющее в какой момент должна запускатья валидация поля
 * @vue-prop {Boolean} removeBtn - Отображать кнопку удаления интервала
 * @vue-data {SVG} DeleteIcon - Иконка корзины
 * @vue-data {Object|Null} localInterval - Локальное состояние интервала
 * @vue-event {Object} change - Интервал изменен
 * @vue-event {void} remove - Удалить интервал
 * */

import DeleteIcon from 'Images/icon/ic-delete.svg'
import { cloneDeep } from 'lodash'
import { requiredRule, time, required } from 'Utils/validate'
import * as webix from 'webix'

export default {
  props: {
    /**
     * Внешнее состояние интервала
     * @type {Object}
     * */
    interval: {
      type: Object,
      required: true
    },
    /**
     * Свойство quasar определяющее в какой момент должна запускатья валидация поля
     * @type {Boolean|String}
     * */
    lazyRules: [Boolean, String],
    /**
     * Отображать кнопку удаления интервала
     * @type {Boolean}
     * */
    removeBtn: Boolean,
    /**
     * Расширение интервалов (стоимости доставки, стоимость заказа)
     * Используется в редактировании зон магазина
     * @type {Boolean}
     */
    withPrices: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      /**
       * Иконка корзины
       * @type {SVG}
       * */
      DeleteIcon,
      /**
       * Локальное состояние интервала
       * @type {Object|Null}
       * */
      localInterval: null
    }
  },
  watch: {
    localInterval: {
      /**
       * Смотртель за локальным состояние интервала
       * Оповещает о изменении интервала
       * @type {Function}
       * @name watch/localInterval
       * @memberOf module:Shared/Component/Interval
       * @fires module:Shared/Component/Interval#change
       * */
      handler() {
        /**
         * Интервал изменен
         * @event module:Shared/Component/Interval#change
         * @type {Object}
         * */
        this.$emit('change', cloneDeep(this.localInterval))
      },
      deep: true
    }
  },
  created() {
    this.$set(this, 'localInterval', cloneDeep(this.interval))
  },
  methods: {
    requiredRule,
    time,
    required,
    /**
     * Обработчик на клик по кнопке с иконкой корзины
     * @fires module:Shared/Component/Interval#remove
     * */
    remove() {
      /**
       * Удалить интервал
       * @event module:Shared/Component/Interval#remove
       * @type {void}
       * */
      webix.confirm({
        title: 'Удалить интервал?',
        ok: 'Да',
        cancel: 'Нет',
        type: 'confirm-warning',
        text: '',
        callback: async result => {
          if (result) {
            this.$emit('remove')
          }
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.interval {
  padding: 10px;
  margin-bottom: 10px;

  display: grid;
  justify-content: space-between;
  column-gap: 12px;

  border: 1px solid rgba(gray, 0.5);

  grid-template-columns: 50px 120px 320px 92px 50px;
  &.with-prices {
    grid-template-columns: 50px 120px 320px 92px repeat(3, minmax(75px, 120px)) 50px;
  }

  &:last-child {
    margin-bottom: 0;
  }

  .icon {
    width: 48px;
    height: 48px;
    border-radius: 48px;
    cursor: pointer;
  }

  .subtitle {
    font-size: 16px;
    font-weight: 500;

    text-align: center;

    margin-bottom: 20px;
  }

  .section {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
}

.enable-section, .delete-section {
  height: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: center !important;
}

.interval-inputs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 10px;
}

.days {
  display: grid;

  grid-template-columns: repeat(7, 1fr);
  column-gap: 5px;

  .day {
    width: 40px;

    display: flex;
    flex-direction: column;
    align-items: center;
    align-content: center;
    justify-content: center;

    .label, .radio {
      margin-bottom: 7px;
    }
  }
}

.input {
  margin-bottom: 0;
}
</style>
